import { httpClient, getAuthToken, API_BASE_URL } from './core';
import { grundHeader } from '../../components/ui/loeschen-mit-grund-dialog';

export interface MigrationConnectionPayload {
  host: string;
  port: number;
  database_name: string;
  username: string;
  password?: string;
  active?: boolean;
}

export interface MigrationConnectionDto {
  standort_id: number;
  host: string;
  port: number;
  database_name: string;
  username: string;
  active: boolean;
  has_password: boolean;
  updated_at?: string;
}

export type MigrationModuleName = 'teachers' | 'persons' | 'history' | 'rooms' | 'trials' | 'tasks' | 'orders';

export interface MigrationDryRunPayload {
  standort_id: number;
  modules?: MigrationModuleName[];
}

export interface MigrationModuleSummary {
  found: number;
  eligible: number;
  will_create: number;
  will_update: number;
  new?: number;
  already_mapped?: number;
  unchanged?: number;
  conflicts: number;
  skipped: number;
  cases_found?: number;
  cases_eligible?: number;
  cases_open?: number;
  archive_found?: number;
  archive_written?: number;
  imported?: number;
  reused?: number;
  inactive?: number;
  skipped_no_active_fach?: number;
  subjects_linked?: number;
  availability_rows?: number;
}

export interface FachMappingUploadResult {
  written: number;
  skipped: number;
  unknown_fach_ids: number[];
}

export interface MigrationDryRunResult {
  standort_id: number;
  run_id?: string;
  modules: Record<string, MigrationModuleSummary>;
  conflicts: Array<{ module: string; code: string; message: string; payload?: Record<string, unknown> }>;
}

export interface MigrationRunStartPayload {
  standort_id: number;
  modules?: MigrationModuleName[];
  force?: boolean;
  auto_send_onboarding?: boolean;
  /** Pflicht: ID des Probelaufs für Standort und Bereiche (T-0219). */
  dry_run_id: string;
}

export interface MigrationRunModuleProgress {
  status?: 'running' | 'done' | 'failed';
  total?: number;
  processed?: number;
  started_at?: string;
  finished_at?: string;
  updated_at?: string;
}

export interface MigrationRunSummary {
  standort_id?: number;
  modules?: Record<string, MigrationRunModuleProgress & Partial<MigrationModuleSummary>>;
  conflicts?: unknown[];
}

export interface MigrationRunStatusDto {
  run_id: string;
  standort_id: number;
  status: 'pending' | 'running' | 'finished' | 'failed' | 'cancelled';
  started_at?: string | null;
  finished_at?: string | null;
  summary?: MigrationRunSummary | null;
}

export interface MigrationRunLogDto {
  id: number;
  module: string;
  level: 'info' | 'warning' | 'error';
  message: string;
  payload?: Record<string, unknown>;
  created_at: string;
}

export interface MigrationCaseLegacyStudentDto {
  first_name?: string | null;
  last_name?: string | null;
  birth_date?: string | null;
  age?: number | null;
}

export interface MigrationCaseLegacyPartnerDto {
  legacy_person_id?: number | null;
  name?: string | null;
}

export interface MigrationCaseLegacyEntityDto {
  id?: number | null;
  name?: string | null;
}

export interface MigrationCaseLegacyContractDto {
  student?: MigrationCaseLegacyStudentDto;
  contract_partner?: MigrationCaseLegacyPartnerDto;
  unterrichtsform?: MigrationCaseLegacyEntityDto;
  teacher?: MigrationCaseLegacyEntityDto;
  fach?: MigrationCaseLegacyEntityDto;
  start?: string | null;
  end?: string | null;
}

export interface MigrationCaseDto {
  id: number;
  standort_id: number;
  legacy_person_id: number;
  legacy_contract_id: number;
  wp_user_id?: number | null;
  wp_user_email?: string | null;
  wp_user_name?: string | null;
  subprofile_id?: string | null;
  status: 'offen' | 'email_gesendet' | 'onboarding_gestartet' | 'migriert' | 'beendet' | 'fehler';
  migration_phase?:
    | 'vertragsanpassung_offen'
    | 'auftragsanpassung_offen'
    | 'angebot_offen'
    | 'angebot_versendet'
    | 'unterricht_bestaetigen'
    | 'kurs_starten'
    | 'vertrag_beenden_offen'
    | 'abgeschlossen'
    | 'geschlossen'
    | string;
  is_favorite?: boolean;
  email_exists_in_system?: boolean;
  is_linked_or_created?: boolean;
  person_detail_wp_user_id?: number | null;
  email_check?: {
    exists: boolean;
    candidate?: string | null;
    matched_wp_user_id?: number | null;
    matched_wp_user_email?: string | null;
  };
  snapshot?: Record<string, unknown> | null;
  token_id?: number | null;
  token_state?: 'open' | 'used' | 'expired' | 'revoked';
  token_expires_at?: string | null;
  last_email_sent_at?: string | null;
  email_last_error?: string | null;
  email_attempts?: number;
  onboarding_started_at?: string | null;
  migrated_at?: string | null;
  legacy_contract?: MigrationCaseLegacyContractDto | null;
  /**
   * Letztes für den Office-User relevantes Ereignis (siehe
   * `migrationEventDisplay.ts` Allow-List). Wird vom Backend pro Case
   * mitgeliefert und auf der Card als „Ereignisse"-Pill gerendert.
   */
  latest_event?: MigrationCaseEventDto | null;
  created_at: string;
  updated_at: string;
}

export interface MigrationOpenSummaryDto {
  standort_id: number;
  count: number;
  by_phase: Record<string, number>;
}

export interface MigrationSettingsDto {
  allowContractTermination: boolean;
}

export interface MigrationCaseSearchParams {
  standort_id: number;
  teacher?: number;
  fach?: number;
  uform?: number;
  status?: string;
  query?: string;
  favorites_only?: boolean;
  limit?: number;
  offset?: number;
}

export interface MigrationCaseLinkDto {
  id: number;
  case_id: number;
  standort_id: number;
  link_type: string;
  source_type: string;
  source_id?: string | null;
  target_type: string;
  target_id?: string | null;
  target_display?: string | null;
  mapping_mode: string;
  payload?: Record<string, unknown> | null;
  actor_user_id?: number | null;
  created_at: string;
  updated_at: string;
}

export interface MigrationCaseLinksResponse {
  links: MigrationCaseLinkDto[];
  suggestions: Array<{
    link_type: string;
    target_type: string;
    target_id: string;
    target_display: string;
    mapping_mode: string;
  }>;
}

export interface MigrationActionPreviewResponse {
  ok: boolean;
  action_id: number;
  warnings: string[];
  communication: {
    email_mandatory: boolean;
    sms_optional: boolean;
  };
}

export interface MigrationActionExecuteResponse {
  ok: boolean;
  action_id: number;
  case_status: string;
  migration_phase: string;
}

export type MigrationPersonAssignmentMode = 'auto' | 'manual' | 'create_new';

export interface MigrationPersonAssignmentSelection {
  mode: MigrationPersonAssignmentMode;
  legacy_person_id?: number | null;
  wp_user_id?: number | null;
  subprofile_id?: string | null;
  label?: string | null;
  draft_profile?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    address_street?: string;
    address_zip?: string;
    address_city?: string;
    birth_date?: string;
  };
}

export interface MigrationContractAdjustmentLessonSelection {
  teacher_wp_user_id: number;
  teacher_name?: string;
  fach_id?: number | null;
  fach_name?: string | null;
  unterrichtsform_id: number;
  unterrichtsform_name?: string;
  weekday: number;
  room_id?: number | null;
  room_name?: string | null;
  start_time?: string | null;
  interval_weeks?: number | null;
  duration_min?: number | null;
}

export interface MigrationContractAdjustmentPackageSelection {
  customer_chooses: boolean;
  selected_package_id?: string | null;
  payment_type?: 'immediate' | 'installment' | null;
  installments?: number | null;
}

export interface MigrationOpenAppointmentsPayload {
  transfer?: boolean;
  lehrer_absagen?: number;
  aussetzen?: number;
  resolution?: 'schedule_before_start' | 'credit' | 'none_open' | 'skip';
}

export interface MigrationLegacyPersonUpdate {
  salutation?: string;
  company_name?: string;
  first_name?: string;
  last_name?: string;
  birth_date?: string;
  email?: string;
  phone?: string;
  address_street?: string;
  address_zip?: string;
  address_city?: string;
  address_country?: string;
  newsletter_opt_in?: boolean;
  preferred_contact_email?: boolean;
  preferred_contact_phone?: boolean;
}

export interface MigrationContractAdjustmentPayload {
  person_assignment: {
    student: MigrationPersonAssignmentSelection;
    contract_partner: MigrationPersonAssignmentSelection;
  };
  lesson: MigrationContractAdjustmentLessonSelection;
  start_date: string;
  package_selection: MigrationContractAdjustmentPackageSelection;
  legacy_updates?: {
    student?: MigrationLegacyPersonUpdate;
    contract_partner?: MigrationLegacyPersonUpdate;
  };
  coupon_code?: string;
  open_appointments?: MigrationOpenAppointmentsPayload;
  history?: {
    description?: string;
  } | null;
  communication: {
    email_mandatory: true;
    sms_optional?: boolean;
    sms_text?: string;
    groups?: Record<string, unknown>;
  };
}

export interface MigrationCaseLinkSubprofile {
  id: string;
  index: number;
  firstName: string;
  lastName: string;
  name: string;
  birthDate?: string | null;
  relationshipType?: string | null;
}

export interface MigrationCaseLinkTarget {
  matched: boolean;
  reason?: 'no_email_in_snapshot' | 'no_user_match' | string;
  email?: string;
  wp_user?: {
    id: number;
    name: string;
    email: string;
    phone: string;
    subprofiles: MigrationCaseLinkSubprofile[];
  };
  case?: {
    id: number;
    wp_user_id: number | null;
    subprofile_id: string | null;
  };
  suggested_new_subprofile?: {
    firstName: string;
    lastName: string;
    birthDate?: string | null;
    name: string;
  };
}

export interface MigrationCaseEventDto {
  id: number;
  case_id: number;
  standort_id: number;
  event_type: string;
  actor_user_id?: number | null;
  actor_name?: string | null;
  payload?: Record<string, unknown> | null;
  created_at: string;
}

export interface MigrationWizardStepData {
  data_check?: {
    confirmed?: boolean;
    notes?: string;
    user_profile?: {
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
      address_street?: string;
      address_zip?: string;
      address_city?: string;
    };
  };
  linked_people_check?: {
    confirmed?: boolean;
    notes?: string;
  };
  courses_history_check?: {
    confirmed?: boolean;
    migrate_courses?: boolean;
    create_missing_courses?: boolean;
    import_history?: boolean;
    notes?: string;
    target_course_id?: number;
    target_teacher_wp_user_id?: number;
    target_unterrichtsform_id?: number;
    target_weekday?: number;
    target_start_time?: string;
  };
}

export interface MigrationWizardDraft {
  step_data?: MigrationWizardStepData;
  contract_adjustment_wizard?: Record<string, unknown>;
  notes?: string | null;
  last_saved_at?: string | null;
  last_saved_by?: number | null;
}

export interface MigrationWizardChecks {
  status_editable: boolean;
  required_steps: string[];
  step_results: Record<string, boolean>;
  blocking_reasons: string[];
  can_confirm: boolean;
  computed: {
    has_wp_user: boolean;
    has_subprofile: boolean;
    legacy_course_id: number;
    legacy_teacher_id: number;
    legacy_uform_id?: number;
    legacy_weekday?: number;
    legacy_start_time?: string | null;
    legacy_begin?: string | null;
    legacy_end?: string | null;
    mapped_course_id?: number | null;
    mapped_teacher_wp_user_id?: number | null;
    mapped_unterrichtsform_id?: number | null;
    mapped_weekday?: number | null;
    mapped_start_time?: string | null;
  };
}

export interface MigrationWizardTargetCourseOption {
  id: number;
  name: string;
  dauer_min?: number | null;
  unterrichtsform_id?: number | null;
  unterrichtsform_name?: string | null;
  teacher_wp_user_id?: number | null;
  teacher_name?: string | null;
  weekday?: number | null;
  time_start?: string | null;
  time_end?: string | null;
}

export interface MigrationWizardTargetTeacherOption {
  wp_user_id: number;
  name: string;
  email?: string | null;
}

export interface MigrationWizardTargetUnterrichtsformOption {
  id: number;
  name: string;
  dauer?: number | null;
  typ?: string | null;
}

export interface MigrationCaseDetailsResponse {
  case: MigrationCaseDto;
  legacy_data?: Record<string, unknown> & {
    legacy_related_people?: Array<{
      role: 'student' | 'contract_partner' | 'payer' | string;
      legacy_person_id: number;
      name?: string | null;
      email?: string | null;
      phone?: string | null;
    }>;
  };
  wizard: MigrationWizardDraft;
  checks: MigrationWizardChecks;
  events: MigrationCaseEventDto[];
  editable: boolean;
  user_profile?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    address_street?: string;
    address_zip?: string;
    address_city?: string;
    subprofile_assignment_text?: string | null;
  };
  target_options?: {
    teachers: MigrationWizardTargetTeacherOption[];
    unterrichtsformen: MigrationWizardTargetUnterrichtsformOption[];
    courses: MigrationWizardTargetCourseOption[];
  };
}

export type ArchiveMatchConfidence = 'high' | 'medium' | 'low';

export interface ArchiveMatchCandidatesQuery {
  standort_id: number;
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  birth_date?: string;
  fach_id?: number;
  instrument?: string;
  limit?: number;
}

export interface ArchivePersonSnapshot {
  name: string;
  email_masked?: string | null;
  phone_masked?: string | null;
  instrument?: string | null;
  last_course_name?: string | null;
  last_course_end?: string | null;
}

export interface ArchiveMatchCandidate {
  archive_record_id: number;
  confidence: ArchiveMatchConfidence;
  match_reason: string;
  score?: number | null;
  person_snapshot: ArchivePersonSnapshot;
}

export interface ArchiveMatchCandidatesResponse {
  matches: ArchiveMatchCandidate[];
  meta: {
    strategy_used: string;
    total: number;
  };
}

export interface ArchiveMatchDecisionPayload {
  standort_id: number;
  archive_record_id: number;
  decision: 'selected' | 'dismissed';
  context?: string;
  entity_id?: number;
  confidence?: ArchiveMatchConfidence;
  reason?: string;
}

export interface ArchiveMatchDecisionResponse {
  ok: boolean;
  event_id?: number;
}

export interface MigrationResetPreviewItem {
  table: string;
  to_delete_count: number;
  missing?: boolean;
}

export interface MigrationResetPreviewResponse {
  standort_id: number;
  mode: 'migration_only' | 'full_domain';
  preview: MigrationResetPreviewItem[];
  /** Mit dieser ID darf der Reset laufen — höchstens eine Stunde (T-0219). */
  preview_id?: string;
}

export interface MigrationResetRunPayload {
  mode: 'migration_only' | 'full_domain';
  force: boolean;
  confirm_text?: string;
  preview_id: string;
}

export interface MigrationOpsKpisDto {
  standort_id: number;
  funnel: {
    offen: number;
    email_gesendet: number;
    onboarding_gestartet: number;
    migriert: number;
    beendet?: number;
    fehler: number;
  };
  median_completion_minutes: number | null;
  archive_count: number;
}

export interface MigrationPreviewEvent {
  case_id: number;
  teacher_wp_user_id: number | null;
  legacy_teacher_id: number | null;
  legacy_teacher_name: string | null;
  day_of_week: number;
  start_time: string;
  end_time: string;
  duration_min: number;
  fach_id: number | null;
  fach_name: string | null;
  uform_id: number | null;
  uform_name: string | null;
  student_name: string | null;
  case_status: string;
  migration_phase: string;
}

export const migrationApi = {
  getLegacyLocations: async (): Promise<Array<{ id: number; name: string }>> => {
    const res = await httpClient<Array<{ id: number; name: string }>>('/api/migration/legacy-locations');
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Legacy-Standorte');
    return res.data;
  },
  listConnections: async (): Promise<MigrationConnectionDto[]> => {
    const res = await httpClient<MigrationConnectionDto[]>('/api/migration/connections');
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Verbindungen');
    return res.data;
  },
  saveConnection: async (standortId: number, payload: MigrationConnectionPayload): Promise<MigrationConnectionDto> => {
    const res = await httpClient<MigrationConnectionDto>(`/api/migration/connections/${encodeURIComponent(String(standortId))}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Speichern der Verbindung');
    return res.data;
  },
  downloadFachMapping: async (standortId: number): Promise<{ blob: Blob; filename: string }> => {
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE_URL}/api/migration/connections/${encodeURIComponent(String(standortId))}/fach-mapping`, {
      method: 'GET',
      credentials: 'include',
      headers,
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const data = await res.json();
        msg = (data as { error?: string; message?: string }).error || (data as { error?: string; message?: string }).message || msg;
      } catch { /* ignore */ }
      throw new Error(msg);
    }
    const filename = `fach-mapping-standort-${standortId}.csv`;
    return { blob: await res.blob(), filename };
  },
  uploadFachMappingCsv: async (standortId: number, csv: string): Promise<FachMappingUploadResult> => {
    const token = getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'text/csv' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE_URL}/api/migration/connections/${encodeURIComponent(String(standortId))}/fach-mapping`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: csv,
    });
    const data = await res.json().catch(() => null) as (FachMappingUploadResult & { error?: string }) | null;
    if (!res.ok) {
      throw new Error(data?.error || `HTTP ${res.status}`);
    }
    if (!data) throw new Error('Fach-Mapping-Antwort konnte nicht gelesen werden');
    return { written: data.written, skipped: data.skipped, unknown_fach_ids: data.unknown_fach_ids ?? [] };
  },
  dryRun: async (payload: MigrationDryRunPayload): Promise<MigrationDryRunResult> => {
    const res = await httpClient<MigrationDryRunResult>('/api/migration/dry-run', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Dry-Run fehlgeschlagen');
    return res.data;
  },
  startImport: async (payload: MigrationRunStartPayload): Promise<{ run_id: string }> => {
    const res = await httpClient<{ run_id: string }>('/api/migration/run', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Import konnte nicht gestartet werden');
    return res.data;
  },
  getRunStatus: async (runId: string): Promise<MigrationRunStatusDto> => {
    const res = await httpClient<MigrationRunStatusDto>(`/api/migration/runs/${encodeURIComponent(runId)}`);
    if (res.error || !res.data) throw new Error(res.error || 'Laufstatus konnte nicht geladen werden');
    return res.data;
  },
  getRunLog: async (runId: string): Promise<MigrationRunLogDto[]> => {
    const res = await httpClient<MigrationRunLogDto[]>(`/api/migration/runs/${encodeURIComponent(runId)}/logs`);
    if (res.error || !res.data) throw new Error(res.error || 'Run-Log konnte nicht geladen werden');
    return res.data;
  },
  listCases: async (params: { standort_id: number; status?: string; limit?: number; offset?: number }): Promise<MigrationCaseDto[]> => {
    const query = new URLSearchParams();
    query.set('standort_id', String(params.standort_id));
    if (params.status) query.set('status', params.status);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.offset !== undefined) query.set('offset', String(params.offset));
    const res = await httpClient<{ items: MigrationCaseDto[] }>(`/api/migration/cases?${query.toString()}`);
    if (res.error || !res.data) throw new Error(res.error || 'Migration-Cases konnten nicht geladen werden');
    return res.data.items || [];
  },
  getOpenSummary: async (standortId: number): Promise<MigrationOpenSummaryDto> => {
    const res = await httpClient<MigrationOpenSummaryDto>(`/api/migration/standorte/open-summary?standort_id=${encodeURIComponent(String(standortId))}`);
    if (res.error || !res.data) throw new Error(res.error || 'Open-Summary konnte nicht geladen werden');
    return res.data;
  },
  getSettings: async (): Promise<MigrationSettingsDto> => {
    const res = await httpClient<MigrationSettingsDto>('/api/migration/settings');
    if (res.error || !res.data) throw new Error(res.error || 'Migration-Einstellungen konnten nicht geladen werden');
    return res.data;
  },
  updateSettings: async (payload: Partial<MigrationSettingsDto>): Promise<MigrationSettingsDto> => {
    const res = await httpClient<MigrationSettingsDto>('/api/migration/settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Migration-Einstellungen konnten nicht gespeichert werden');
    return res.data;
  },
  searchCases: async (params: MigrationCaseSearchParams): Promise<MigrationCaseDto[]> => {
    const query = new URLSearchParams();
    query.set('standort_id', String(params.standort_id));
    if (params.teacher !== undefined) query.set('teacher', String(params.teacher));
    if (params.fach !== undefined) query.set('fach', String(params.fach));
    if (params.uform !== undefined) query.set('uform', String(params.uform));
    if (params.status) query.set('status', params.status);
    if (params.query) query.set('query', params.query);
    if (params.favorites_only !== undefined) query.set('favorites_only', params.favorites_only ? '1' : '0');
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.offset !== undefined) query.set('offset', String(params.offset));
    const res = await httpClient<{ items: MigrationCaseDto[] }>(`/api/migration/cases/search?${query.toString()}`);
    if (res.error || !res.data) throw new Error(res.error || 'Migration-Cases konnten nicht geladen werden');
    return res.data.items || [];
  },
  listFavorites: async (standortId: number): Promise<number[]> => {
    const res = await httpClient<{ case_ids: number[] }>(`/api/migration/favorites?standort_id=${encodeURIComponent(String(standortId))}`);
    if (res.error || !res.data) throw new Error(res.error || 'Favoriten konnten nicht geladen werden');
    return res.data.case_ids || [];
  },
  setFavorite: async (caseId: number): Promise<void> => {
    const res = await httpClient<{ ok: boolean }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/favorite`, {
      method: 'PUT',
      body: JSON.stringify({}),
    } as RequestInit);
    if (res.error) throw new Error(res.error || 'Favorit konnte nicht gesetzt werden');
  },
  removeFavorite: async (caseId: number): Promise<void> => {
    // Läuft über die Catch-all-Route der Migration, die für DELETE den Lösch-Key
    // verlangt (RBAC D.8). Einen Favoriten abwählen ist keine echte Löschung —
    // deshalb ein fester Grund statt einer Rückfrage.
    const res = await httpClient<{ ok: boolean }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/favorite`, {
      method: 'DELETE',
      ...grundHeader('Favorit abgewählt'),
    } as RequestInit);
    if (res.error) throw new Error(res.error || 'Favorit konnte nicht entfernt werden');
  },
  getCaseLinks: async (caseId: number): Promise<MigrationCaseLinksResponse> => {
    const res = await httpClient<MigrationCaseLinksResponse>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/links`);
    if (res.error || !res.data) throw new Error(res.error || 'Links konnten nicht geladen werden');
    return res.data;
  },
  upsertCaseLink: async (caseId: number, linkType: string, payload: Record<string, unknown>): Promise<{ ok: boolean; links: MigrationCaseLinkDto[]; resolved_wp_user_id?: number }> => {
    const res = await httpClient<{ ok: boolean; links: MigrationCaseLinkDto[]; resolved_wp_user_id?: number }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/links/${encodeURIComponent(linkType)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Link konnte nicht gespeichert werden');
    return res.data;
  },
  previewContractAdjustment: async (caseId: number, payload: MigrationContractAdjustmentPayload): Promise<MigrationActionPreviewResponse> => {
    const res = await httpClient<MigrationActionPreviewResponse>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/contract-adjustments/preview`, {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Preview fehlgeschlagen');
    return res.data;
  },
  executeContractAdjustment: async (caseId: number, payload: MigrationContractAdjustmentPayload): Promise<MigrationActionExecuteResponse> => {
    const res = await httpClient<MigrationActionExecuteResponse>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/contract-adjustments/execute`, {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Ausführung fehlgeschlagen');
    return res.data;
  },
  previewContractTermination: async (caseId: number, payload: Record<string, unknown>): Promise<MigrationActionPreviewResponse> => {
    const res = await httpClient<MigrationActionPreviewResponse>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/contract-terminations/preview`, {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Preview fehlgeschlagen');
    return res.data;
  },
  executeContractTermination: async (caseId: number, payload: Record<string, unknown>): Promise<MigrationActionExecuteResponse> => {
    const res = await httpClient<MigrationActionExecuteResponse>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/contract-terminations/execute`, {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Ausführung fehlgeschlagen');
    return res.data;
  },
  previewOrderAdjustment: async (caseId: number, payload: Record<string, unknown>): Promise<MigrationActionPreviewResponse> => {
    const res = await httpClient<MigrationActionPreviewResponse>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/order-adjustments/preview`, {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Preview fehlgeschlagen');
    return res.data;
  },
  executeOrderAdjustment: async (caseId: number, payload: Record<string, unknown>): Promise<MigrationActionExecuteResponse> => {
    const res = await httpClient<MigrationActionExecuteResponse>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/order-adjustments/execute`, {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Ausführung fehlgeschlagen');
    return res.data;
  },
  sendCaseEmail: async (caseId: number): Promise<{ ok: boolean; status?: string; error?: string | null }> => {
    const res = await httpClient<{ ok: boolean; status?: string; error?: string | null }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/send-email`, {
      method: 'POST',
      body: JSON.stringify({}),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Case-Mail konnte nicht versendet werden');
    return res.data;
  },
  resendCaseEmail: async (caseId: number): Promise<{ ok: boolean; status?: string; error?: string | null }> => {
    const res = await httpClient<{ ok: boolean; status?: string; error?: string | null }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/resend-email`, {
      method: 'POST',
      body: JSON.stringify({}),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Case-Mail konnte nicht erneut versendet werden');
    return res.data;
  },
  confirmCaseMigration: async (caseId: number, payload?: Record<string, unknown>): Promise<{ ok: boolean; status?: string }> => {
    const res = await httpClient<{ ok: boolean; status?: string }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/confirm`, {
      method: 'POST',
      body: JSON.stringify(payload ?? {}),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Case konnte nicht migriert werden');
    return res.data;
  },
  finalizeCase: async (caseId: number): Promise<{ ok: boolean; enrollment_id: number | null; order_id: number | null; offer_sent: boolean; errors: string[] }> => {
    const res = await httpClient<{ ok: boolean; enrollment_id: number | null; order_id: number | null; offer_sent: boolean; errors: string[] }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/finalize`, {
      method: 'POST',
      body: JSON.stringify({}),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Vertrag konnte nicht finalisiert werden');
    return res.data;
  },
  sendCaseOffer: async (caseId: number, data: {
    email: { subject: string; html: string; text?: string; vars?: Record<string, unknown> };
    history?: { description?: string };
    offer: { honorarCents: number; lessonCount: number; totalValueCents: number; currency: string };
  }): Promise<{ ok: boolean; enrollment_id: number | null; order_id: number | null; teacher_order_id?: number | null; offer_sent: boolean; errors: string[] }> => {
    const res = await httpClient<{ ok: boolean; enrollment_id: number | null; order_id: number | null; teacher_order_id?: number | null; offer_sent: boolean; errors: string[] }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/send-offer`, {
      method: 'POST',
      body: JSON.stringify(data),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Angebot konnte nicht versendet werden');
    return res.data;
  },
  getCaseDetails: async (caseId: number): Promise<MigrationCaseDetailsResponse> => {
    const res = await httpClient<MigrationCaseDetailsResponse>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/details`);
    if (res.error || !res.data) throw new Error(res.error || 'Case-Details konnten nicht geladen werden');
    return res.data;
  },
  checkCaseWizard: async (caseId: number, payload?: { step_data?: MigrationWizardStepData }): Promise<MigrationWizardChecks> => {
    const res = await httpClient<MigrationWizardChecks>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/checks`, {
      method: 'POST',
      body: JSON.stringify(payload ?? {}),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Wizard-Checks konnten nicht ausgeführt werden');
    return res.data;
  },
  saveCaseWizardDraft: async (caseId: number, payload: { step_data?: MigrationWizardStepData; notes?: string | null; contract_adjustment_wizard?: Record<string, unknown> }): Promise<{ ok: boolean; event_id?: number; wizard: MigrationWizardDraft; checks: MigrationWizardChecks }> => {
    const res = await httpClient<{ ok: boolean; event_id?: number; wizard: MigrationWizardDraft; checks: MigrationWizardChecks }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/draft`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Wizard-Entwurf konnte nicht gespeichert werden');
    return res.data;
  },
  previewCaseWizardConfirm: async (caseId: number, payload?: { step_data?: MigrationWizardStepData }): Promise<{ ok: boolean; event_id?: number | null; checks: MigrationWizardChecks; can_confirm: boolean }> => {
    const res = await httpClient<{ ok: boolean; event_id?: number | null; checks: MigrationWizardChecks; can_confirm: boolean }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/preview-confirm`, {
      method: 'POST',
      body: JSON.stringify(payload ?? {}),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Wizard-Vorschau konnte nicht geladen werden');
    return res.data;
  },
  getArchiveMatchCandidates: async (queryInput: ArchiveMatchCandidatesQuery): Promise<ArchiveMatchCandidatesResponse> => {
    const query = new URLSearchParams();
    Object.entries(queryInput).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value));
      }
    });
    const res = await httpClient<ArchiveMatchCandidatesResponse>(`/api/migration/archive/match-candidates?${query.toString()}`);
    if (res.error || !res.data) throw new Error(res.error || 'Archiv-Matches konnten nicht geladen werden');
    return res.data;
  },
  logArchiveMatchDecision: async (payload: ArchiveMatchDecisionPayload): Promise<ArchiveMatchDecisionResponse> => {
    const res = await httpClient<ArchiveMatchDecisionResponse>('/api/migration/archive/match-decisions', {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Match-Entscheidung konnte nicht gespeichert werden');
    return res.data;
  },
  getResetPreview: async (standortId: number, mode: 'migration_only' | 'full_domain'): Promise<MigrationResetPreviewResponse> => {
    const res = await httpClient<MigrationResetPreviewResponse>(`/api/migration/reset/${encodeURIComponent(String(standortId))}/preview?mode=${encodeURIComponent(mode)}`);
    if (res.error || !res.data) throw new Error(res.error || 'Reset-Vorschau konnte nicht geladen werden');
    return res.data;
  },
  runReset: async (standortId: number, payload: MigrationResetRunPayload): Promise<Record<string, unknown>> => {
    const res = await httpClient<Record<string, unknown>>(`/api/migration/reset/${encodeURIComponent(String(standortId))}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Reset konnte nicht ausgeführt werden');
    return res.data;
  },
  getOpsKpis: async (standortId: number): Promise<MigrationOpsKpisDto> => {
    const res = await httpClient<MigrationOpsKpisDto>(`/api/migration/ops/kpis?standort_id=${encodeURIComponent(String(standortId))}`);
    if (res.error || !res.data) throw new Error(res.error || 'Ops-KPIs konnten nicht geladen werden');
    return res.data;
  },
  getCaseEvents: async (caseId: number): Promise<MigrationCaseEventDto[]> => {
    const res = await httpClient<{ events: MigrationCaseEventDto[] }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/events`);
    if (res.error || !res.data) throw new Error(res.error || 'Ereignis-Historie konnte nicht geladen werden');
    return res.data.events ?? [];
  },
  addCaseComment: async (caseId: number, text: string): Promise<MigrationCaseEventDto[]> => {
    const res = await httpClient<{ ok: boolean; events: MigrationCaseEventDto[] }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/comment`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Kommentar konnte nicht gespeichert werden');
    return res.data.events ?? [];
  },
  getCaseLinkTarget: async (caseId: number): Promise<MigrationCaseLinkTarget> => {
    const res = await httpClient<MigrationCaseLinkTarget>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/link-target`);
    if (res.error || !res.data) throw new Error(res.error || 'Verknüpfungs-Daten konnten nicht geladen werden');
    return res.data;
  },
  linkCaseToCustomer: async (
    caseId: number,
    body: {
      wp_user_id: number;
      subprofile_id?: string | null;
      new_subprofile?: { firstName: string; lastName: string; birthDate?: string | null } | null;
    }
  ): Promise<{ ok: boolean; wp_user_id: number; subprofile_id: string | null; subprofile_was_created: boolean }> => {
    const res = await httpClient<{ ok: boolean; wp_user_id: number; subprofile_id: string | null; subprofile_was_created: boolean }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/link`, {
      method: 'POST',
      body: JSON.stringify(body),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Verknüpfung fehlgeschlagen');
    return res.data;
  },
  recordContactAttempt: async (caseId: number, outcome: 'not_reached' | 'reached'): Promise<MigrationCaseEventDto[]> => {
    const res = await httpClient<{ ok: boolean; events: MigrationCaseEventDto[] }>(`/api/migration/cases/${encodeURIComponent(String(caseId))}/contact-attempt`, {
      method: 'POST',
      body: JSON.stringify({ outcome }),
    } as RequestInit);
    if (res.error || !res.data) throw new Error(res.error || 'Kontaktversuch konnte nicht gespeichert werden');
    return res.data.events ?? [];
  },
  createMigrationCoupon: async (
    caseId: number,
    body: { valueCents: number; wpUserId: number },
  ): Promise<{ id: number; code: string; valueCents: number }> => {
    const res = await httpClient<{ id: number; code: string; valueCents: number }>(
      `/api/migration/cases/${encodeURIComponent(String(caseId))}/migration-coupon`,
      {
        method: 'POST',
        body: JSON.stringify({
          valueCents: body.valueCents,
          linkedWpUserId: body.wpUserId,
        }),
      } as RequestInit,
    );
    if (res.error || !res.data) throw new Error(res.error || 'Gutscheincode konnte nicht erstellt werden');
    return res.data;
  },
  getPreviewEvents: async (standortId: number, teacherWpUserId?: number): Promise<MigrationPreviewEvent[]> => {
    const params = new URLSearchParams();
    params.set('standort_id', String(standortId));
    if (teacherWpUserId !== undefined && teacherWpUserId > 0) {
      params.set('teacher_wp_user_id', String(teacherWpUserId));
    }
    const res = await httpClient<{ events: MigrationPreviewEvent[] }>(`/api/migration/preview-events?${params.toString()}`);
    if (res.error || !res.data) throw new Error(res.error || 'Migrations-Vorschau konnte nicht geladen werden');
    return res.data.events ?? [];
  },
};
